import {
    Autocomplete,
    Dropdown,
    Field as DropdownField,
    Hint as DropdownHint,
    Label as DropdownLabel,
    Item,
    Menu
} from "@zendeskgarden/react-dropdowns";
import {Field, Input, Label, Message} from "@zendeskgarden/react-forms";
import {Pagination} from "@zendeskgarden/react-pagination";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {ReactComponent as UserIcon} from "@zendeskgarden/svg-icons/src/12/user-solo-stroke.svg";
import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from "react";
import {
    AdminSearchAuditLogUsersQuery,
    useAdminSearchAuditLogLazyQuery,
    useAdminSearchAuditLogUsersLazyQuery
} from "../../../__generated__/generated_types";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {AuthContext} from "../../../contexts/AuthContext";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import AdminHeader from "../AdminHeader";
import {AdminWrapper} from "../AdminWrapper";
import {API_MAPPINGS, mapApiName} from "./api-mappings";
import {renderAuditLogUsername} from "./common";
import {RenderAuditLogVariables} from "./RenderAuditLogVariables";
import * as css from "./styles/AuditLog.module.scss";


const ANY_USER_ID = "any";

export function AuditLog(): ReactElement {
    const {properties} = useProperties();
    const {accounts} = useChartOfAccounts();
    const {user, operationalMetricTypes} = useContext(AuthContext);

    const [searchAuditLog, {data, loading}] = useAdminSearchAuditLogLazyQuery({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });

    const [searchUsers, {data: users, loading: usersLoading}] = useAdminSearchAuditLogUsersLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [accountId, setAccountId] = useState<string | null>(null);
    const [propertyId, setPropertyId] = useState<string | null>(null);
    const [operationNames, setOperationNames] = useState<string[]>(Object.keys(API_MAPPINGS));
    const [beforeTime, setBeforeTime] = useState<string | null>(null);
    const [afterTime, setAfterTime] = useState<string | null>(null);
    const [userId, setUserId] = useState<string>(ANY_USER_ID);
    const [userObj, setUserObj] = useState<AdminSearchAuditLogUsersQuery["searchAuditTrailUser"][0] | undefined>(undefined);
    const [page, setPage] = useState<number>(1);
    const pageRef = useRef<number>(1);
    const initialLoadRef = useRef<boolean>(true);

    const [userSearch, setUserSearch] = useState<string>("");
    const [foundUsers, setFoundUsers] = useState<AdminSearchAuditLogUsersQuery | undefined>(undefined);
    const [searchingUsers, setSearchingUsers] = useState<boolean>(false);

    useEffect(() => {
        if(!userSearch) {
            setFoundUsers(undefined);
            setSearchingUsers(false);
            return;
        }

        setSearchingUsers(true);
        const to = setTimeout(() => {
            searchUsers({
                variables: {
                    searchTerm: userSearch,
                    max: 10
                }
            });
        }, 300);

        return () => clearTimeout(to);
    }, [userSearch]);

    useEffect(() => {
        if(!usersLoading) {
            setSearchingUsers(false);
            setFoundUsers(users);
        }
    }, [users, usersLoading]);


    useEffect(() => {
        if(operationNames.length === 0) {
            return;
        }

        let noWait = false;
        if(initialLoadRef.current) {
            noWait = true;
        } else if(pageRef.current !== page) {
            noWait = true;
        }

        initialLoadRef.current = false;
        pageRef.current = page;

        const to = setTimeout(() => {
            searchAuditLog({
                variables: {
                    accountId: accountId ?? undefined,
                    propertyId: propertyId ?? undefined,
                    operationNames: operationNames ?? undefined,
                    userId: userId === ANY_USER_ID ? undefined : userId,
                    beforeTime: beforeTime ? new Date(beforeTime).toISOString() : undefined,
                    afterTime: afterTime ? new Date(afterTime).toISOString() : undefined,
                    page: page
                }
            });
        }, noWait ? 0 : 800);
        return () => clearTimeout(to);
    }, [accountId, propertyId, operationNames, beforeTime, afterTime, userId, page]);

    const propertyOptions = useMemo(() => {
        const options = properties?.map(p => ({label: p.name, value: p.id})) ?? [];
        options.unshift({
            label: "None",
            value: "null"
        });
        return options;
    }, [properties]);

    const accountOptions = useMemo(() => {
        const options = accounts?.map(a => ({label: a.name, value: a.id})) ?? [];
        options.unshift({
            label: "None",
            value: "null"
        });
        return options;
    }, [accounts]);

    const operationOptions = useMemo(() => {
        return Object.entries(API_MAPPINGS)
                .map(([name, operation]) => ({label: operation, value: name}))
                .sortBy(o => o.label);
    }, [API_MAPPINGS]);

    function renderUserSearch(
        userSearch: string,
        users: AdminSearchAuditLogUsersQuery | undefined,
        usersLoading: boolean
    ): ReactElement {
        if(usersLoading) {
            return <Item disabled>Searching...</Item>;
        }

        const anyUser = <Item key={ANY_USER_ID} value={ANY_USER_ID}>Any User</Item>;
        if((!users?.searchAuditTrailUser || users.searchAuditTrailUser.length === 0) && userSearch !== "") {
            return <>
                {anyUser}
                <Item disabled>No Users Found.</Item>
            </>;
        }

        return <>
            {anyUser}
            {users?.searchAuditTrailUser && users.searchAuditTrailUser.map(u => <Item key={u.id} value={u.id}>{renderAuditLogUsername(u)}</Item>)}
        </>;
    }

    function findUserObj(
        userId: string,
        users: AdminSearchAuditLogUsersQuery["searchAuditTrailUser"] | undefined
    ): AdminSearchAuditLogUsersQuery["searchAuditTrailUser"][0] | undefined {
        if(!users) {
            return undefined;
        }
        return users.find(u => u.id === userId);
    }

    return <AdminWrapper>
        <AdminHeader title="Search Audit Log" subtitle="" />
        <div className={css.searchForm}>
            <form>
                <div className={css.sideBySide}>
                    <Field>
                        <Label>Before</Label>
                        <Input
                            type="datetime-local"
                            value={beforeTime ?? ""}
                            onChange={(e) => {
                                if(e.target.value) {
                                    setBeforeTime(e.target.value);
                                } else {
                                    setBeforeTime(null);
                                }
                            }}
                        />
                    </Field>
                    <Field>
                        <Label>After</Label>
                        <Input
                            type="datetime-local"
                            value={afterTime ?? ""}
                            onChange={(e) => {
                                if(e.target.value) {
                                    setAfterTime(e.target.value);
                                } else {
                                    setAfterTime(null);
                                }
                            }}
                        />
                    </Field>
                </div>
                <div className={css.sideBySide}>
                    <Field>
                        <Label>Property</Label>
                        <CustomZDDropdown
                                applySelectedItems={selected => {
                                    const sel = selected[0];
                                    if(!sel || sel === "null") {
                                        setPropertyId(null);
                                    } else {
                                        setPropertyId(sel);
                                    }
                                }}
                                openDropdownPlaceholder="Select Property..."
                                closedDropdownPlaceholder={undefined}
                                options={propertyOptions}
                                isError={false}
                                initialSelectedItems={propertyId ? [propertyId] : ["null"]}
                        />
                    </Field>
                    <Field>
                        <Label>Account</Label>
                        <CustomZDDropdown
                                applySelectedItems={selected => {
                                    const sel = selected[0];
                                    if(!sel || sel === "null") {
                                        setAccountId(null);
                                    } else {
                                        setAccountId(sel);
                                    }
                                }}
                                openDropdownPlaceholder="Select Property..."
                                closedDropdownPlaceholder={undefined}
                                options={accountOptions}
                                isError={false}
                                initialSelectedItems={accountId ? [accountId] : ["null"]}
                        />
                    </Field>
                </div>
                <div className={css.sideBySide}>
                    <Field>
                        <Label>Operations</Label>
                        <CustomZDDropdown
                                applySelectedItems={selected => {
                                    if(Array.isArray(selected)) {
                                        const opNames: string[] = selected.filter(s => typeof s === "string" && !!API_MAPPINGS[s]);
                                        setOperationNames(opNames);
                                    }
                                }}
                                openDropdownPlaceholder="Select Operation(s)..."
                                closedDropdownPlaceholder={undefined}
                                options={operationOptions}
                                isError={operationNames.length === 0}
                                isMulti={true}
                                initialSelectedItems={operationNames}
                                allOption={true}
                        />
                        {operationNames.length === 0 && <Message>At least one operation must be selected.</Message>}
                    </Field>
                    <Dropdown
                        inputValue={userSearch}
                        onInputValueChange={val => setUserSearch(val)}
                        selectedItem={userId}
                        onSelect={item => {
                            setUserObj(findUserObj(item, foundUsers?.searchAuditTrailUser));
                            setUserId(item);
                        }}
                    >
                        <DropdownField className={css.userAutoComplete}>
                            <DropdownLabel>User</DropdownLabel>
                            <Autocomplete start={<UserIcon />}>
                                {userId === ANY_USER_ID ?
                                    "Any User" :
                                    (userObj ? renderAuditLogUsername(userObj) : `${userId}`)
                                }
                            </Autocomplete>
                            <DropdownHint>Type a name to search users.</DropdownHint>
                        </DropdownField>
                        <Menu>
                            {renderUserSearch(userSearch, foundUsers, searchingUsers)}
                        </Menu>
                    </Dropdown>
                </div>
            </form>
        </div>
        <div>
            <nav aria-label="Results Page Navigation">
                <Pagination currentPage={page} totalPages={data?.searchAuditTrail.maxPage ?? 1} onChange={newPage => setPage(newPage)} />
            </nav>
            <Table className={css.resultTable}>
                <Head>
                    <HeaderRow>
                        <HeaderCell className={css.actionUser}>Action Time</HeaderCell>
                        <HeaderCell className={css.actionUser}>User</HeaderCell>
                        <HeaderCell>Operation</HeaderCell>
                        <HeaderCell>Accounts</HeaderCell>
                        <HeaderCell>Properties</HeaderCell>
                        <HeaderCell className={css.variables}>Variables</HeaderCell>
                    </HeaderRow>
                </Head>
                <Body>
                    {loading ?
                        <Row>
                            <Cell colSpan={6}>Loading...</Cell>
                        </Row> :
                        !data?.searchAuditTrail ?
                                <Row>
                                    <Cell colSpan={6}>No Results.</Cell>
                                </Row> :
                                data.searchAuditTrail.items.map((item, idx) => {
                                    return <Row key={idx}>
                                        <Cell className={css.actionUser}>
                                            <div>{new Date(item.performedAt).toLocaleDateString()}</div>
                                            <div>{new Date(item.performedAt).toLocaleTimeString()}</div>
                                        </Cell>
                                        <Cell className={css.actionUser}>{renderAuditLogUsername(item.user)}</Cell>
                                        <Cell>{mapApiName(item.operationName)}</Cell>
                                        <Cell>
                                            <div className={css.verticalCell}>
                                                {item.accounts.sortBy("name").dedupe("id").map(a => {
                                                    return <div className={css.nameVertical} key={a.id}>
                                                        {a.number ?
                                                            `${a.number} - ${a.name}` :
                                                            a.name
                                                        }
                                                    </div>;
                                                })}
                                            </div>
                                        </Cell>
                                        <Cell>
                                            <div className={css.verticalCell}>
                                                {item.properties.sortBy("name").dedupe("id").map(p => {
                                                    return <div className={css.nameVertical} key={p.id}>{p.name}</div>;
                                                })}
                                            </div>
                                        </Cell>
                                        <Cell className={css.variables}>
                                            {user && item.variables &&
                                                <div className={css.verticalCell}>
                                                    <RenderAuditLogVariables
                                                        user={user}
                                                        operationName={item.operationName}
                                                        variables={item.variables}
                                                        accounts={accounts}
                                                        properties={properties ?? []}
                                                        operationalMetricTypes={operationalMetricTypes}
                                                        enrichment={item.enrichment ?? undefined}
                                                     />
                                                </div>
                                            }
                                        </Cell>
                                    </Row>;
                                })
                    }
                </Body>
            </Table>
        </div>
    </AdminWrapper>;
}